// Dynamic URL creatives (HTML pages served in an iframe, like zip creatives).
// - URL with {screenId}: one creative per screen, the placeholder replaced by
//   the Taggify screen id (external_id, e.g. "tgx_5564") before saving. The
//   bidder never sees the placeholder: it would replace {screenId} with an
//   internal screen id. Each creative is pinned to its screen.
// - URL without macros: a single creative shared by every compatible screen.

export const DYNAMIC_URL_SOURCE = 'url';
export const DYNAMIC_URL_PLAYER_ID = 1;

export const SUPPORTED_URL_MACROS = [
    { token: '{screenId}', example: 'tgx_5564' },
];

const SCREEN_PLACEHOLDER = /\{\s*screen_?id\s*\}/gi;

export const hasScreenPlaceholder = (template) =>
    new RegExp(SCREEN_PLACEHOLDER.source, 'i').test(String(template ?? ''));

export const isValidUrlTemplate = (template) => {
    const value = String(template ?? '').trim();
    if (!value || /\s/.test(value)) return false;

    try {
        // Any scheme is accepted; only the URL shape is validated.
        new URL(value.replace(SCREEN_PLACEHOLDER, 'screen'));
        return true;
    } catch {
        return false;
    }
};

export const getScreenExternalId = (screen) => {
    const value = screen?.external_id ?? screen?.externalId;
    return typeof value === 'string' || typeof value === 'number'
        ? String(value).trim()
        : '';
};

export const resolveUrlTemplate = (template, externalId) =>
    String(template).trim().replace(SCREEN_PLACEHOLDER, externalId);

export const isDynamicUrlCreative = (properties) =>
    properties?.source === DYNAMIC_URL_SOURCE;

const normalizeId = (value) => String(value ?? '').trim().toLowerCase();

// Pinned creatives only fit the screen they were built for; shared ones (no
// pinned screen) fit any screen, like zip creatives.
export const isScreenAllowedForCreative = (creativeProperties, screen) => {
    if (
        !isDynamicUrlCreative(creativeProperties) ||
        !creativeProperties.pinnedScreenId
    ) {
        return true;
    }

    return (
        normalizeId(getScreenExternalId(screen)) ===
        normalizeId(creativeProperties.pinnedScreenId)
    );
};

export const getUrlCreativeName = (url) => {
    const value = String(url ?? '').trim();
    const lastSegment = value.split(/[?#]/)[0].split('/').filter(Boolean).pop();
    return lastSegment || value;
};

/**
 * Splits the selected screens into the ones that support dynamic creatives
 * (dynamic player and external id, deduplicated) and the skipped ones.
 */
export const getDynamicUrlScreens = (screens = []) => {
    const eligible = [];
    const skipped = [];
    const seen = new Set();

    for (const screen of screens) {
        const externalId = getScreenExternalId(screen);

        if (screen?.player?.id !== DYNAMIC_URL_PLAYER_ID) {
            skipped.push({ screen, externalId, reason: 'player' });
            continue;
        }

        if (!externalId) {
            skipped.push({ screen, externalId, reason: 'external-id' });
            continue;
        }

        if (seen.has(externalId)) continue;
        seen.add(externalId);
        eligible.push({ screen, externalId });
    }

    return { eligible, skipped };
};

/**
 * Builds the creatives to create from a URL: one per eligible screen when the
 * URL has {screenId}, otherwise a single one for all of them.
 */
export const buildDynamicUrlCreatives = (template, screens = []) => {
    const { eligible, skipped } = getDynamicUrlScreens(screens);
    const perScreen = hasScreenPlaceholder(template);

    if (!perScreen) {
        const url = String(template).trim();
        return {
            perScreen,
            eligible,
            skipped,
            items: eligible.length
                ? [{ url, name: getUrlCreativeName(url) }]
                : [],
        };
    }

    return {
        perScreen,
        eligible,
        skipped,
        items: eligible.map(({ screen, externalId }) => ({
            screen,
            externalId,
            pinnedScreenId: externalId,
            name: externalId,
            url: resolveUrlTemplate(template, externalId),
        })),
    };
};
